import type { ConsentState, ConsentCategory } from "./types";

const COOKIE_NAME = "uk_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 395; // 13 months in seconds

const DEFAULT_CONSENT: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
};

function parseCookie(): ConsentState | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

export function getConsent(): ConsentState {
  return parseCookie() || DEFAULT_CONSENT;
}

export function hasConsent(category: ConsentCategory): boolean {
  if (category === "necessary") return true;
  return getConsent()[category];
}

export function hasConsentBeenSet(): boolean {
  return parseCookie() !== null;
}

export function setConsent(consent: Partial<ConsentState>): void {
  const current = getConsent();
  const updated: ConsentState = {
    ...current,
    ...consent,
    necessary: true, // always on
  };

  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(updated))}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;

  updateGTMConsent(updated);

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("uk:consent-changed"));
  }
}

export function updateGTMConsent(consent: ConsentState): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];

  // Consent Mode v2 only recognizes updates via the gtag arguments pseudo-array
  // (gtag('consent','update',{...})). A plain dataLayer.push({event:...}) is
  // ignored and leaves analytics_storage at the default 'denied'.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as Record<string, unknown>);
  }
  gtag("consent", "update", {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
  });
}
